import { useCallback, useEffect, useState } from "react"
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  Divider,
  IconButton,
  LinearProgress,
  Link,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material"
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined"
import ExpandIcon from "@mui/icons-material/ChevronRightOutlined"
import ReportIcon from "@mui/icons-material/InsertChartOutlined"

import dayjs from "dayjs"

import { timeReportApi, clientApi, projectApi, userApi } from "../api"
import { usePermissions } from "../permissions"
import PageHeader from "../components/PageHeader"
import EmptyState from "../components/EmptyState"
import { LoadingRows } from "../components/LoadingState"
import ClientSelect from "../components/ClientSelect"
import ProjectSelect from "../components/ProjectSelect"
import PersonSelect from "../components/PersonSelect"
import QuickFilters from "../components/QuickFilters"
import DateRangePicker, { describeRange, presetRanges } from "../components/DateRangePicker"
import HoursChart from "../components/HoursChart"
import UserAvatar from "../components/UserAvatar"
import SplitButton from "../components/SplitButton"
import Figure, { deltaOf } from "../components/Figure"
import ProjectDetailDialog from "./projects/ProjectDetailDialog"
import ClientDetailDialog from "./clients/ClientDetailDialog"
import PersonDialog from "./users/PersonDialog"
import { hoursLabel as asHours } from "../time/duration"
import BillableIcon from "@mui/icons-material/PaidOutlined"
import SignedIcon from "@mui/icons-material/VerifiedOutlined"

/**
 * Where the hours went.
 *
 * ── The three questions, as tabs ────────────────────────────────────────────
 *
 * By client, by project, by person. They are the same query grouped three ways,
 * and they are three different conversations: what a contract is costing, where
 * a project's effort went, and what somebody's week looked like. A fourth, by
 * day, is here because the shape over time is what tells you whether a number
 * is a trend or one bad week.
 *
 * ── Why two hour columns, always ────────────────────────────────────────────
 *
 * Tracked is what the clock said. Billable is what may be invoiced — lower
 * whenever somebody wrote a duration down or marked an entry non-billable. A
 * report showing one and labelling it the other is how a project looks
 * profitable right up until the invoice goes out.
 */

/* This month, because a report that opens on all time opens on a number nobody
   asked for and takes a second to compute. */
const DEFAULT_RANGE = presetRanges().find((preset) => preset.id === "this_month")

const GROUPS = [
  { id: "client", label: "By client", heading: "Client" },
  { id: "project", label: "By project", heading: "Project" },
  { id: "user", label: "By person", heading: "Person" },
  { id: "day", label: "By day", heading: "Day" },
]

/* What a row opens into. Mirrors the server's own pairing, so the heading and
   the rows underneath it cannot describe different things. */
const BREAKDOWN_LABEL = {
  client: "By client",
  project: "By project",
  user: "By person",
  day: "By day",
}

const VIEWS = [
  { key: "billable_only", label: "Billable only", icon: BillableIcon },
  { key: "signed_only", label: "Signed off only", icon: SignedIcon },
]

export default function TimeReportPage() {
  const { can, isBackOffice } = usePermissions()

  const [groupBy, setGroupBy] = useState("client")
  const [range, setRange] = useState({ from: DEFAULT_RANGE.from, to: DEFAULT_RANGE.to })
  const [filters, setFilters] = useState({ client_id: "", project_id: "", user_id: "", billable_only: false, signed_only: false })
  const [report, setReport] = useState(null)
  const [clients, setClients] = useState([])
  const [projects, setProjects] = useState([])
  const [people, setPeople] = useState([])
  const [loading, setLoading] = useState(true)
  const [exporting, setExporting] = useState(false)
  /* Keyed by row, so two open rows stay open and reopening one does not refetch
     what is already on screen. */
  const [expanded, setExpanded] = useState({})
  const [breakdowns, setBreakdowns] = useState({})
  const [error, setError] = useState(null)
  const [notice, setNotice] = useState(null)
  /* What a row opens. The report keeps its range, its filters and its scroll
     position underneath — which is the whole reason these are panels over the
     table rather than links away from it. */
  const [openProjectId, setOpenProjectId] = useState(null)
  const [openClientId, setOpenClientId] = useState(null)
  const [openPerson, setOpenPerson] = useState(null)

  const params = useCallback(() => {
    const query = { group_by: groupBy }
    if (range.from) query.from = range.from
    if (range.to) query.to = range.to
    if (filters.client_id) query.client_id = filters.client_id
    if (filters.project_id) query.project_id = filters.project_id
    if (filters.user_id) query.user_id = filters.user_id
    if (filters.billable_only) query.billable = "true"
    if (filters.signed_only) query.signed = "true"
    return query
  }, [groupBy, range, filters])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setReport(await timeReportApi.summary(params()))
      /* Cleared with the parent rows: a breakdown fetched under last month's
         filters, still open under this month's, is a wrong number presented
         with total confidence. */
      setExpanded({})
      setBreakdowns({})
    } catch (err) {
      setError(err.userMessage)
    } finally {
      setLoading(false)
    }
  }, [params])

  useEffect(() => {
    load()
  }, [load])

  const searchPeople = useCallback((query) => {
    userApi
      .directory(query || undefined)
      .then((data) => setPeople(data.users || []))
      .catch(() => {})
  }, [])

  useEffect(() => {
    projectApi
      .list({ limit: 200 })
      .then((data) => setProjects(data.projects || []))
      .catch(() => setProjects([]))
    if (isBackOffice && can("clients", "view")) {
      clientApi
        .list()
        .then((data) => setClients(data.organizations || []))
        .catch(() => setClients([]))
    }
  }, [isBackOffice, can])

  const toggleRow = async (row) => {
    const key = String(row.group_key)
    if (expanded[key]) {
      setExpanded((current) => ({ ...current, [key]: false }))
      return
    }
    setExpanded((current) => ({ ...current, [key]: true }))
    if (breakdowns[key]) return
    try {
      const data = await timeReportApi.breakdown({ ...params(), group_key: key })
      setBreakdowns((current) => ({ ...current, [key]: data }))
    } catch {
      /* Left undefined rather than set to an empty list: the row can then say
         it could not load, where an empty list would claim there is nothing
         inside a client with forty hours against it. */
      setBreakdowns((current) => ({ ...current, [key]: null }))
    }
  }

  /* Tab-separated: it pastes into a spreadsheet cell by cell and still reads
     as columns in a plain-text message. */
  const copyAsText = async () => {
    const lines = [
      [heading, "Tracked", "Billable"].join("\t"),
      ...groups.map((row) => [row.group_label, asHours(row.tracked_seconds), asHours(row.billable_seconds)].join("\t")),
      ["Total", asHours(totals?.tracked_seconds), asHours(totals?.billable_seconds)].join("\t"),
    ]
    try {
      await navigator.clipboard.writeText(lines.join("\n"))
      setNotice("Copied to the clipboard.")
    } catch {
      setError("This browser would not let the page use the clipboard.")
    }
  }

  const download = async () => {
    setExporting(true)
    setError(null)
    try {
      await timeReportApi.exportCsv(params())
    } catch (err) {
      setError(err.userMessage)
    } finally {
      setExporting(false)
    }
  }

  const groups = report?.groups || []
  const totals = report?.totals
  const before = report?.previous?.totals
  const heading = GROUPS.find((group) => group.id === groupBy)?.heading || "Group"
  const filtersActive =
    Boolean(filters.client_id || filters.project_id || filters.user_id) || filters.billable_only || filters.signed_only

  /* The longest bar is the scale. Against a fixed maximum every row on a quiet
     week is a stub, and the comparison between rows is the whole point. */
  const busiest = groups.reduce((most, row) => Math.max(most, row.tracked_seconds), 0)

  return (
    <Box>
      <PageHeader
        title="Time reports"
        subtitle="Hours totalled by client, project or person, over a range you choose."
        actions={
          can("time_reports", "export") ? (
            <SplitButton
              startIcon={<DownloadIcon />}
              disabled={exporting || loading || groups.length === 0}
              /* Export is nearly always the spreadsheet; pasting the figures
                 into a message or a ticket is the other half of what people do
                 with a report, and it needs no round trip. */
              options={[
                { label: exporting ? "Preparing…" : "Export CSV", run: download },
                { label: "Copy as text", run: copyAsText },
              ]}
            />
          ) : null
        }
      />

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      <Snackbar
        open={Boolean(notice)}
        autoHideDuration={3000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert severity="success" variant="filled" sx={{ boxShadow: 6 }}>
          {notice}
        </Alert>
      </Snackbar>

      {/*
        * Said out loud when the numbers are only the reader's own.
        *
        * Without `view_all` every figure here is one person's. A page that
        * prints that under the heading "Time reports" is lying by omission to
        * everybody who shares a project.
        */}
      {report?.scope === "own" ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          These are your own hours. Seeing the whole team's needs the “view all” permission on Time Reports.
        </Alert>
      ) : null}

      <Card sx={{ mb: 2 }}>
        <Tabs
          value={groupBy}
          onChange={(event, value) => setGroupBy(value)}
          sx={{ px: 1.5, borderBottom: 1, borderColor: "divider" }}
        >
          {GROUPS.map((group) => (
            <Tab key={group.id} value={group.id} label={group.label} />
          ))}
        </Tabs>

        <Box sx={{ p: "12px 14px" }}>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center">
            <DateRangePicker from={range.from} to={range.to} onChange={setRange} />

            {isBackOffice && clients.length > 0 ? (
              <ClientSelect
                clients={clients}
                value={filters.client_id}
                onChange={(id) => setFilters((current) => ({ ...current, client_id: id, project_id: "" }))}
                emptyLabel="All clients"
                sx={{ minWidth: 180 }}
              />
            ) : null}

            <ProjectSelect
              projects={
                filters.client_id
                  ? projects.filter((project) => String(project.client_id) === String(filters.client_id))
                  : projects
              }
              value={filters.project_id}
              onChange={(id) => setFilters((current) => ({ ...current, project_id: id }))}
              sx={{ minWidth: 180 }}
            />

            {/* Only where it can do anything: without `view_all` every row is
                already this one person, and a picker that cannot change the
                answer is a control that lies about what it does. */}
            {can("time_reports", "view_all") ? (
              <PersonSelect
                people={people}
                label="Person"
                value={filters.user_id}
                onChange={(id) => setFilters((current) => ({ ...current, user_id: id }))}
                onSearch={searchPeople}
                emptyLabel="Everyone"
                sx={{ minWidth: 180 }}
              />
            ) : null}
          </Stack>

          <Divider sx={{ my: 1.25 }} />

          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center">
            <QuickFilters
              items={VIEWS}
              values={filters}
              onToggle={(key) => setFilters((current) => ({ ...current, [key]: !current[key] }))}
            />
            <Box sx={{ flex: 1 }} />
            {filtersActive ? (
              <Button
                size="small"
                onClick={() =>
                  setFilters({ client_id: "", project_id: "", user_id: "", billable_only: false, signed_only: false })
                }
              >
                Clear filters
              </Button>
            ) : null}
            <Typography variant="caption" color="text.secondary">
              {describeRange(range.from, range.to)}
            </Typography>
          </Stack>
        </Box>
      </Card>

      {/*
        * The shape, before the numbers.
        *
        * Thirty-two hours spread over a month and thirty-two hours in one
        * frantic Tuesday are the same total and completely different facts,
        * and only one of them is a resourcing problem. Shown whenever the rows
        * are days; on the other groupings the share column in the table is
        * already the comparison and a second bar chart would repeat it.
        */}
      {groupBy === "day" && groups.length > 1 ? (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
              Tracked hours per day
            </Typography>
            <HoursChart
              points={[...groups]
                /* Chronological, whatever the table's order. The table sorts by
                   size because the biggest row is the answer there; a time axis
                   sorted by size is not a time axis. */
                .sort((a, b) => String(a.group_key).localeCompare(String(b.group_key)))
                .map((row) => ({
                  key: row.group_key,
                  value: row.tracked_seconds,
                  hint: dayjs(row.group_key).format("MMM D"),
                  display: asHours(row.tracked_seconds),
                  secondary: `${asHours(row.billable_seconds)} billable`,
                }))}
            />
          </CardContent>
        </Card>
      ) : null}

      {/* The three figures somebody came for, before the table they came from.
          A total under a hundred rows is a total nobody scrolls to. */}
      {totals ? (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
          <Figure
            label="Tracked"
            value={asHours(totals.tracked_seconds)}
            caption={`${totals.entry_count} entries`}
            delta={deltaOf(totals.tracked_seconds, before?.tracked_seconds, asHours)}
          />
          <Figure
            label="Billable"
            value={asHours(totals.billable_seconds)}
            delta={deltaOf(totals.billable_seconds, before?.billable_seconds, asHours)}
            caption={
              totals.tracked_seconds > 0
                ? `${Math.round((totals.billable_seconds / totals.tracked_seconds) * 100)}% of tracked`
                : "nothing tracked"
            }
          />
          <Figure
            label={groupBy === "user" ? "People" : "People involved"}
            value={String(totals.people || 0)}
            caption={`across ${groups.length} ${heading.toLowerCase()}${groups.length === 1 ? "" : "s"}`}
          />
        </Stack>
      ) : null}

      <Card>
        {loading ? (
          <CardContent>
            <LoadingRows rows={5} />
          </CardContent>
        ) : groups.length === 0 ? (
          <EmptyState
            icon={ReportIcon}
            title="No hours in this range"
            description="Nothing was logged against this selection. Widen the dates, or clear the filters."
          />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{heading}</TableCell>
                <TableCell align="right">Entries</TableCell>
                <TableCell align="right">Tracked</TableCell>
                <TableCell align="right">Billable</TableCell>
                <TableCell align="right">Signed off</TableCell>
                <TableCell sx={{ width: "22%" }}>Share</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map((row) => {
                const key = String(row.group_key)
                const open = Boolean(expanded[key])
                const inner = breakdowns[key]
                return (
                <>
                <TableRow key={key} hover>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      {/* Only where the row IS a person. A face beside a client
                          name is a face that belongs to nobody. */}
                      {groupBy === "user" ? (
                        <UserAvatar name={row.group_label} avatarKey={row.avatar_key} size={24} tooltip={false} sx={{ mr: 0.5 }} />
                      ) : null}
                      {/* The whole row is not the target: the label is
                          selectable text and people copy client names out of
                          reports. A disclosure triangle is the affordance, and
                          it is a real button so the keyboard reaches it. */}
                      <IconButton
                        size="small"
                        onClick={() => toggleRow(row)}
                        aria-label={`${open ? "Collapse" : "Expand"} ${row.group_label}`}
                        aria-expanded={open}
                        sx={{
                          p: 0.25,
                          transform: open ? "rotate(90deg)" : "none",
                          transition: "transform 160ms ease",
                        }}
                      >
                        <ExpandIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                      <Box sx={{ minWidth: 0 }}>
                    {/* A client, a project or a person is a thing with a screen
                        behind it, so the label opens it — the same panels the
                        Clients, Projects and Users pages open, rather than a
                        second, thinner version here. A day is a date. */}
                    <RowLabel
                      onOpen={
                        groupBy === "project" && row.group_key
                          ? () => setOpenProjectId(Number(row.group_key))
                          : groupBy === "user" && row.group_key
                            ? () => setOpenPerson({ id: Number(row.group_key), name: row.group_label })
                            : groupBy === "client" && row.group_key != null
                              ? () => setOpenClientId(String(row.group_key))
                              : null
                      }
                      title={groupBy === "project" ? "Open this project" : groupBy === "client" ? "Open this client" : "Who this is"}
                    >
                      {row.group_label}
                    </RowLabel>
                    {/* A project's code, and whose project it is — the client
                        opens too. Under "By project" the client was the one
                        thing missing, and three projects called "Monthly
                        Bookkeeping" are told apart by nothing else. */}
                    <Stack direction="row" spacing={0.6} alignItems="baseline" sx={{ minWidth: 0 }}>
                      {row.reference && String(row.reference) !== String(row.group_label) && groupBy !== "client" ? (
                        <Typography variant="caption" color="text.secondary">
                          {row.reference}
                        </Typography>
                      ) : null}
                      {groupBy === "project" && row.client_id != null && row.client_label ? (
                        <>
                          {row.reference ? (
                            <Typography variant="caption" color="text.disabled">
                              ·
                            </Typography>
                          ) : null}
                          <Link
                            component="button"
                            type="button"
                            variant="caption"
                            onClick={() => setOpenClientId(String(row.client_id))}
                            title="Open this client"
                            sx={{ color: "text.secondary", textDecorationColor: "currentColor", "&:hover": { color: "primary.dark" } }}
                          >
                            {row.client_label}
                          </Link>
                        </>
                      ) : null}
                    </Stack>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                    {row.entry_count}
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>
                    {asHours(row.tracked_seconds)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                    {asHours(row.billable_seconds)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                    {asHours(row.signed_seconds)}
                  </TableCell>
                  <TableCell>
                    {/* Relative to the busiest row, so the column is a
                        comparison rather than decoration. The figure is already
                        in the cells to its left; this is only the shape. */}
                    <Tooltip title={`${Math.round((row.tracked_seconds / (busiest || 1)) * 100)}% of the busiest`}>
                      <LinearProgress
                        variant="determinate"
                        value={busiest ? (row.tracked_seconds / busiest) * 100 : 0}
                        sx={{ height: 6, borderRadius: 3 }}
                      />
                    </Tooltip>
                  </TableCell>
                </TableRow>

                {/* Collapse rather than a conditional: a block that appears
                    instantly shoves every row below it out from under the
                    reader's eyes. */}
                <TableRow key={`${key}-inner`}>
                  <TableCell colSpan={6} sx={{ py: 0, border: 0 }}>
                    <Collapse in={open} timeout={200} unmountOnExit>
                      <Box sx={{ py: 1.5, pl: 5, pr: 1 }}>
                        {inner === undefined ? (
                          <LoadingRows rows={2} height={28} />
                        ) : inner === null ? (
                          <Typography variant="caption" color="error">
                            That breakdown could not be loaded.
                          </Typography>
                        ) : inner.groups.length === 0 ? (
                          <Typography variant="caption" color="text.secondary">
                            Nothing to break this down by.
                          </Typography>
                        ) : (
                          <Stack spacing={0.75}>
                            <Typography variant="caption" color="text.secondary" fontWeight={700}>
                              {BREAKDOWN_LABEL[inner.group_by] || "Breakdown"}
                            </Typography>
                            {inner.groups.map((child) => (
                              <Stack key={child.group_key} direction="row" alignItems="center" spacing={1.5}>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  {/* Expanding a client lists its projects, and
                                      a project named in a report is a project
                                      somebody wants to look at. Same for the
                                      people inside a project. */}
                                  <RowLabel
                                    weight={500}
                                    onOpen={
                                      inner.group_by === "project" && child.group_key
                                        ? () => setOpenProjectId(Number(child.group_key))
                                        : inner.group_by === "user" && child.group_key
                                          ? () => setOpenPerson({ id: Number(child.group_key), name: child.group_label })
                                          : null
                                    }
                                    title={inner.group_by === "project" ? "Open this project" : "Who this is"}
                                  >
                                    {child.group_label}
                                  </RowLabel>
                                </Box>
                                <LinearProgress
                                  variant="determinate"
                                  value={row.tracked_seconds ? (child.tracked_seconds / row.tracked_seconds) * 100 : 0}
                                  sx={{ width: 120, height: 5, borderRadius: 3 }}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{ width: 84, textAlign: "right", fontVariantNumeric: "tabular-nums" }}
                                >
                                  {asHours(child.tracked_seconds)}
                                </Typography>
                              </Stack>
                            ))}
                          </Stack>
                        )}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
                </>
                )
              })}

              {totals ? (
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                    {totals.entry_count}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                    {asHours(totals.tracked_seconds)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                    {asHours(totals.billable_seconds)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                    {asHours(totals.signed_seconds)}
                  </TableCell>
                  <TableCell />
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        )}
      </Card>

      <ProjectDetailDialog projectId={openProjectId} onClose={() => setOpenProjectId(null)} />
      <ClientDetailDialog clientId={openClientId} onClose={() => setOpenClientId(null)} />
      <PersonDialog
        userId={openPerson?.id || null}
        fallbackName={openPerson?.name}
        onClose={() => setOpenPerson(null)}
      />
    </Box>
  )
}

/**
 * A row's name, which sometimes opens something.
 *
 * One component for both states so a label that cannot be opened is laid out
 * identically to one that can — otherwise the table's first column shifts by a
 * pixel or two between groupings, which reads as the page redrawing itself.
 */
function RowLabel({ children, onOpen, title, weight = 600 }) {
  if (!onOpen) {
    return (
      <Typography variant="body2" fontWeight={weight} noWrap>
        {children}
      </Typography>
    )
  }
  return (
    <Tooltip title={title} describeChild>
      <Box
        component="button"
        type="button"
        onClick={onOpen}
        sx={{
          appearance: "none",
          border: 0,
          background: "none",
          p: 0,
          font: "inherit",
          fontWeight: weight,
          textAlign: "left",
          cursor: "pointer",
          color: "primary.dark",
          display: "block",
          maxWidth: "100%",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          borderRadius: 0.5,
          "&:hover": { textDecoration: "underline" },
          "&:focus-visible": { outline: (theme) => `2px solid ${theme.palette.primary.dark}`, outlineOffset: 2 },
        }}
      >
        {children}
      </Box>
    </Tooltip>
  )
}

